import { useRef, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import { ToastContainer } from 'react-toastify';

import { useAPIContext } from '../api/context';
import Icon from '../components/Icon';
import dialogs from '../dialogs';
import scroll from '../utils/scroll';
import toast from '../utils/toast';

import logo from '../assets/logo.svg';
import styles from './Base.module.scss';

/**
 * Skip to main content button.
 */
const Skip = () => {

  return (
    <div className={styles.skip}>
      <a href='#main'>
        Skip to Main Content
      </a>
    </div>
  );
};

/**
 * Menu item under the top level or under a dropdown.
 */
const Item = ({ link, action, caption, style }) => {

  const location = useLocation();
  const path = location && location.pathname;

  if (link) {
    return (
      <Link
        role='menuitem'
        to={link}
        className={`
          ${styles.item}
          ${path === link ? styles.active : ''}
          ${style || ''}
        `} >

        {caption}

      </Link>
    );
  }

  if (action) {
    return (
      <button
        onClick={action}
        className={`
          ${styles.item}
          ${style || ''}
        `}>

        {caption}

      </button>
    );
  }
};

/**
 * Dropdown menu with an icon.
 */
const Drop = ({ icon, style, children }) => {

  const ref = useRef(null);
  const [ active, setActive ] = useState(false);

  const _open = () => {
    setActive(!active);
  };

  const _blur = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setActive(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', _blur, true);
    return () => document.removeEventListener('click', _blur, true);
  }, []);

  return (
    <>
      <button
        ref={ref}
        role='menuitem'
        aria-label={active ? 'close menu' : 'open menu'}
        aria-expanded={active + ''}
        aria-controls='dropdown'
        aria-haspopup='menu'
        className={`
          ${styles.item}
          ${styles.icon}
          ${active ? styles.active : ''}
          ${style || ''}
        `}
        onClick={_open}>

        <Icon name={icon} />

      </button>

      <div
        id='dropdown'
        role='menu'
        className={`${styles.drop} ${active ? styles.active : ''}`}>

        {children}

      </div>
    </>
  );
};

/**
 * Menu for anonymous user.
 */
const AnonMenu = () => {

  const { setDialog } = dialogs.utils.useDialog();

  return (
    <>
      <Item
        link='/'
        caption='Brands'
        style={styles.laptop}
      />

      <Item
        link='/creators'
        caption='Creators'
        style={styles.laptop}
      />

      <Item
        action={() => setDialog('login')}
        caption='Log In'
        style={styles.laptop}
      />

      <Drop icon='menu' style={`${styles.tablet} ${styles.mobile}`}>
        <Item
          link='/'
          caption='Brands'
          style={`${styles.tablet} ${styles.mobile}`}
        />

        <Item
          link='/creators'
          caption='Creators'
          style={`${styles.tablet} ${styles.mobile}`}
        />

        <Item
          action={() => setDialog('login')}
          caption='Log In'
          style={`${styles.tablet} ${styles.mobile}`}
        />
      </Drop>
    </>
  );
};

/**
 * Menu for authenticated user.
 */
const UserMenu = () => {
  return (
    <>
      <Item
        link='/'
        caption='Brands'
        style={styles.laptop}
      />

      <Item
        link='/creators'
        caption='Creators'
        style={styles.laptop}
      />

      <Drop icon='person' style={styles.always}>
        <Item
          link='/campaigns'
          caption='Campaigns'
          style={styles.always}
        />

        <Item
          link='/profile'
          caption='Profile'
          style={styles.always}
        />

        <Item
          link='/'
          caption='Brands'
          style={`${styles.tablet} ${styles.mobile}`}
        />

        <Item
          link='/creators'
          caption='Creators'
          style={`${styles.tablet} ${styles.mobile}`}
        />
      </Drop>
    </>
  );
};

/**
 * Links for the footer.
 */
const FooterMenu = () => {
  return (
    <ul>
      <li>
        <a href='https://forms.gle/CH38ej7YT4kCnHPx8'>
          Do Not Sell My Personal Info
        </a>
      </li>

      <li className={styles.sep}></li>

      <li>
        <a href='https://forms.gle/CH38ej7YT4kCnHPx8'>
          Data
        </a>
      </li>

      <li className={styles.sep}></li>

      <li>
        <Link to='/terms-and-conditions'>
          Terms &amp; Conditions
        </Link>
      </li>

      <li className={styles.sep}></li>

      <li>
        <Link to='/cookie-policy'>
          Cookie Policy
        </Link>
      </li>

      <li className={styles.sep}></li>

      <li>
        <Link to='/privacy-policy'>
          Privacy Policy
        </Link>
      </li>
    </ul>
  );
};

/**
 * Cookie consent.
 */
const Consent = () => {

  const { setConsent } = useAPIContext();

  const accept = (event) => {
    setConsent(true);
  };

  return (
    <div className={styles.consent}>
      <div className={styles.text}>
        Cookies help us deliver our services. By using our
        website, you agree to our use of cookies. 
      </div>

      <div className={styles.actions}>
        <button
          className='skip'
          aria-label='accept the use of cookies'
          onClick={accept}>

            OK

        </button>
      </div>
    </div>
  );
};

/**
 * Base layout with header and footer.
 */
const Base = ({ children, auth }) => {

  /*
   * LOGIN
   */

  const navigate = useNavigate();
  const location = useLocation();
  const path = location && (location.pathname + location.search);

  // Call `login()` to redirect the user to authenticate.
  const login = useCallback(() => {
    navigate(`/?dialog=login&target=${encodeURIComponent(path || '')}`);
  }, [navigate, path]);

  /*
   * AUTH
   */

  const { token, setToken, consent } = useAPIContext();

  // When the `auth` property is set to true, require a token.
  useEffect(() => {
    if (auth && !token) {
      login();
    }
  }, [auth, token, login]);

  // Intercept unauthorized and forbidden responses.
  useEffect(() => {
    window.pipe = (res) => {

      const status = res.status;

      if (status === 401 || status === 403) {
        setToken(null);
        login();
      }

      if (status === 500 || status === 501 || status === 502) {
        toast.show(toast.KIND.ERROR, res.body);
      }

      return res;
    };
  }, [setToken, login]);

  /*
   * LAYOUT
   */

  const { dialog } = dialogs.utils.useDialog();

  return (
    <div className={styles.root}>

      <Skip />

      <scroll.Auto />

      { dialog === 'contact' ? <dialogs.sales.Contact /> : null }

      { dialog === 'login' ? <dialogs.auth.Login /> : null }
      { dialog === 'signup' ? <dialogs.auth.Signup /> : null }
      { dialog === 'forgot' ? <dialogs.auth.Forgot /> : null }
      { dialog === 'forgot-confirm' ? <dialogs.auth.ForgotConfirm /> : null }

      <ToastContainer />

      { !consent && <Consent /> }

      <div
        className={styles.header}
        role='navigation'>

        <div className={styles.logo}>
          <Link
            to='/'
            aria-label='home'>

            <img
              src={logo}
              alt='Chimeable'
            />

          </Link>
        </div>

        <div
          className={styles.menu}
          role='menu'>

          {
            token
            ? <UserMenu />
            : <AnonMenu />
          }
        </div>
      </div>

      <div className={styles.content} role='main' id='main'>
        {children}
      </div>

      <div className={styles.footer} role='contentinfo'>
        <div className={styles.brand}>
          Copyright &copy; 2025 Chimeable. All Rights Reserved.
        </div>

        <div className={styles.links}>
          <FooterMenu />
        </div>
      </div>
    </div>
  );
};

export default Base;